import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Database, Upload, Download, Loader2, FileArchive, FileText,
  Trash2, AlertTriangle, MapPin, Users, CheckCircle2, Globe2, Map as MapIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMasterData() {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem("admin_token");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [replaceAll, setReplaceAll] = useState(false);
  const [shpFile, setShpFile] = useState(null);
  const [dbfFile, setDbfFile] = useState(null);
  const [shxFile, setShxFile] = useState(null);
  const [exporting, setExporting] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvReplaceAll, setCsvReplaceAll] = useState(false);
  const csvInputRef = useRef(null);
  const fileInputRefs = useRef({});

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/master/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setStats(res.data);
    } catch (err) {
      // Do NOT auto-navigate on 401. Admin only logs out via "Keluar" button.
      if (err?.response?.status === 401) {
        toast.error("Gagal memuat statistik master (unauthorized).");
      } else {
        toast.error("Gagal memuat statistik master.");
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken, navigate]);

  useEffect(() => {
    if (!adminToken) { navigate("/admin/login"); return; }
    fetchStats();
  }, [fetchStats, adminToken, navigate]);

  const handleFile = (kind, file) => {
    if (!file) return;
    if (kind === "shp" && !file.name.toLowerCase().endsWith(".shp")) {
      toast.error("File harus berekstensi .shp"); return;
    }
    if (kind === "dbf" && !file.name.toLowerCase().endsWith(".dbf")) {
      toast.error("File harus berekstensi .dbf"); return;
    }
    if (kind === "shx" && !file.name.toLowerCase().endsWith(".shx")) {
      toast.error("File harus berekstensi .shx"); return;
    }
    if (kind === "shp") setShpFile(file);
    else if (kind === "dbf") setDbfFile(file);
    else setShxFile(file);
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!shpFile || !dbfFile) {
      toast.error("File .shp dan .dbf wajib diupload."); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("shp_file", shpFile);
      fd.append("dbf_file", dbfFile);
      if (shxFile) fd.append("shx_file", shxFile);
      const res = await axios.post(
        `${API}/admin/master/import-shapefile?replace_all=${replaceAll}`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        }
      );
      toast.success(res.data?.message || "Impor selesai.");
      setShpFile(null); setDbfFile(null); setShxFile(null);
      Object.values(fileInputRefs.current).forEach(el => { if (el) el.value = ""; });
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal import shapefile.");
    } finally {
      setUploading(false);
    }
  };

  const handleImportCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) { toast.error("Pilih file CSV terlebih dulu."); return; }
    setCsvUploading(true);
    try {
      const fd = new FormData();
      fd.append("csv_file", csvFile);
      const res = await axios.post(
        `${API}/admin/master/import-csv?replace_all=${csvReplaceAll}`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        }
      );
      toast.success(res.data?.message || "Impor CSV selesai.");
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = "";
      fetchStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal import CSV.");
    } finally {
      setCsvUploading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await axios.get(
        `${API}/admin/master/export?format=${format}`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob",
          timeout: 120000,
        }
      );
      const ext = format === "shapefile" ? "zip" : format === "geojson" ? "geojson" : format;
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pdam_tebo_master_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} berhasil.`);
    } catch (err) {
      toast.error("Gagal export data.");
    } finally {
      setExporting("");
    }
  };

  const handleClear = async () => {
    if (!window.confirm("PERHATIAN! Ini akan menghapus SEMUA data master pelanggan. Lanjut?")) return;
    if (!window.confirm("Yakin? Aksi ini tidak bisa dibatalkan.")) return;
    try {
      const res = await axios.delete(`${API}/admin/master/clear`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success(`Berhasil hapus ${res.data?.deleted || 0} record.`);
      fetchStats();
    } catch (err) {
      toast.error("Gagal hapus master.");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-master-data">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
              <Database className="w-6 h-6 text-cyan-600" />
              Master Data Pelanggan (Shapefile)
            </h1>
            <p className="text-sm text-slate-500">
              Import data dari QGIS (.shp + .dbf) lalu jadikan sumber utama Nama/Alamat di Map Pin Drop & Peta Global.
            </p>
          </div>
          <Button onClick={() => navigate("/peta-global")} className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="open-peta-global-btn">
            <Globe2 className="w-4 h-4" /> Buka Peta Global
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-xs text-slate-500">Total Master</div>
            <div className="text-2xl font-bold text-slate-800 mt-1" data-testid="stat-total">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (stats?.total ?? 0).toLocaleString()}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-slate-500">Punya Koordinat</div>
            <div className="text-2xl font-bold text-cyan-700 mt-1" data-testid="stat-coords">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (stats?.with_coordinates ?? 0).toLocaleString()}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-slate-500">Status Aktif</div>
            <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="stat-active">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (stats?.active ?? 0).toLocaleString()}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-slate-500">Impor Terakhir</div>
            <div className="text-xs font-mono text-slate-700 mt-1.5" data-testid="stat-last-import">
              {loading ? "..." : (stats?.last_imported_at ? new Date(stats.last_imported_at).toLocaleString("id-ID") : "Belum pernah")}
            </div>
          </CardContent></Card>
        </div>

        {/* Import */}
        <Card>
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <Upload className="w-5 h-5 text-cyan-600" /> Import Shapefile (QGIS)
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              Upload file <code className="bg-slate-100 px-1 rounded">.shp</code> + <code className="bg-slate-100 px-1 rounded">.dbf</code> hasil export QGIS.
              File <code className="bg-slate-100 px-1 rounded">.shx</code> opsional.
              Field yang diambil: <strong>nomor_pela, nama, alamat_bil, kecamatan, wilayah, rayon, golongan, status, nomor_telp</strong>.
            </p>

            <form onSubmit={handleImport} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FilePicker
                  label=".shp (geometri)"
                  required
                  accept=".shp"
                  file={shpFile}
                  onChange={(f) => handleFile("shp", f)}
                  inputRef={(el) => (fileInputRefs.current.shp = el)}
                  testid="shp-file-input"
                />
                <FilePicker
                  label=".dbf (atribut)"
                  required
                  accept=".dbf"
                  file={dbfFile}
                  onChange={(f) => handleFile("dbf", f)}
                  inputRef={(el) => (fileInputRefs.current.dbf = el)}
                  testid="dbf-file-input"
                />
                <FilePicker
                  label=".shx (index, opsional)"
                  accept=".shx"
                  file={shxFile}
                  onChange={(f) => handleFile("shx", f)}
                  inputRef={(el) => (fileInputRefs.current.shx = el)}
                  testid="shx-file-input"
                />
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
                <div className="font-semibold flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Cara kerja import:
                </div>
                <ul className="list-disc ml-5 space-y-0.5">
                  <li><b>Tanpa centang</b> di bawah → <b>data lama tetap ada</b>. ID yang sama akan di-update; ID baru ditambahkan.</li>
                  <li><b>Dicentang</b> → semua data hasil shapefile dihapus dulu, lalu import baru. Pelanggan yang Anda tambah <b>manual lewat Peta Global tidak ikut hilang</b>.</li>
                </ul>
              </div>

              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={replaceAll}
                  onChange={(e) => setReplaceAll(e.target.checked)}
                  className="mt-0.5"
                  data-testid="replace-all-checkbox"
                />
                <span className="leading-tight">
                  Hapus semua data lama dulu (replace_all)<br/>
                  <span className="text-xs text-slate-500">Centang ini hanya jika ingin ganti total dataset master.</span>
                </span>
              </label>

              <Button
                type="submit"
                className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                disabled={uploading || !shpFile || !dbfFile}
                data-testid="import-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Mengimpor..." : "Mulai Impor"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Import CSV */}
        <Card>
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-emerald-600" /> Import CSV
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              Upload file <code className="bg-slate-100 px-1 rounded">.csv</code> dengan kolom yang sama seperti hasil Export CSV:
              <strong> customer_id, nama, alamat, kecamatan, wilayah, rayon, golongan, status, nomor_telp, latitude, longitude</strong>.
              Data di-upsert berdasarkan <strong>customer_id</strong>.
            </p>

            <form onSubmit={handleImportCsv} className="space-y-3">
              <FilePicker
                label="File CSV"
                required
                accept=".csv"
                file={csvFile}
                onChange={(f) => {
                  if (f && !f.name.toLowerCase().endsWith(".csv")) {
                    toast.error("File harus berekstensi .csv"); return;
                  }
                  setCsvFile(f);
                }}
                inputRef={(el) => (csvInputRef.current = el)}
                testid="csv-file-input"
              />

              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={csvReplaceAll}
                  onChange={(e) => setCsvReplaceAll(e.target.checked)}
                  className="mt-0.5"
                  data-testid="csv-replace-all-checkbox"
                />
                <span className="leading-tight">
                  Hapus data impor lama dulu (replace_all)<br/>
                  <span className="text-xs text-slate-500">Centang untuk mengganti total dataset hasil impor (pin manual tetap aman).</span>
                </span>
              </label>

              <Button
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                disabled={csvUploading || !csvFile}
                data-testid="import-csv-btn"
              >
                {csvUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {csvUploading ? "Mengimpor..." : "Mulai Impor CSV"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Export */}
        <Card>
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <Download className="w-5 h-5 text-cyan-600" /> Export Data Master
            </h2>
            <p className="text-xs text-slate-500 mb-3">
              Download seluruh data master pelanggan (dengan koordinat GPS) dalam berbagai format.
            </p>
            <div className="flex flex-wrap gap-2">
              <ExportButton onClick={() => handleExport("shapefile")} loading={exporting === "shapefile"}
                icon={<FileArchive className="w-4 h-4" />} label="Shapefile (.zip)" testid="export-shp" />
              <ExportButton onClick={() => handleExport("geojson")} loading={exporting === "geojson"}
                icon={<MapPin className="w-4 h-4" />} label="GeoJSON" testid="export-geojson" />
              <ExportButton onClick={() => handleExport("kml")} loading={exporting === "kml"}
                icon={<MapIcon className="w-4 h-4" />} label="KML (Google Earth)" testid="export-kml" />
              <ExportButton onClick={() => handleExport("csv")} loading={exporting === "csv"}
                icon={<FileText className="w-4 h-4" />} label="CSV" testid="export-csv" />
            </div>
          </CardContent>
        </Card>

        {/* Danger zone */}
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="p-5">
            <h2 className="font-semibold text-red-700 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5" /> Zona Bahaya
            </h2>
            <p className="text-xs text-red-600 mb-3">
              Hapus seluruh data master. Aksi ini tidak bisa dibatalkan.
            </p>
            <Button variant="outline" onClick={handleClear}
              className="border-red-300 text-red-700 hover:bg-red-50 gap-2" data-testid="clear-master-btn">
              <Trash2 className="w-4 h-4" /> Hapus Semua Master
            </Button>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function FilePicker({ label, accept, file, onChange, required, inputRef, testid }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100 border border-slate-200 rounded-md p-1 bg-white"
        data-testid={testid}
      />
      {file && (
        <div className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> {file.name} ({Math.round(file.size / 1024)} KB)
        </div>
      )}
    </label>
  );
}

function ExportButton({ onClick, loading, icon, label, testid }) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      disabled={loading}
      className="gap-2 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
      data-testid={testid}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
      {label}
    </Button>
  );
}
